/**
 * Parses the stored coveragesAndLimits XML from a master certificate's
 * coverage entries back into Acord25Form prefill state.
 *
 * NowCerts returns coverage XML in its own tag schema (e.g. <WorkersCompensation>,
 * <AutomobileLiability>) which differs from the AcordFieldItem field names used by
 * get-coverages. This utility bridges that gap so edit mode can restore all saved
 * values without an extra API round-trip.
 */

export interface StoredCoverage {
  databaseId: string;
  policyNumber: string;
  naiCid: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
  type: number;
  acordFormNumber: number;
  coveragesAndLimits: string;
  signatureId: string | null;
}

export interface StoredPolicy {
  databaseId: string;
  number: string;
  effectiveDate: string;
  expirationDate: string;
  linesOfBusiness: string[];
  carrierName: string | null;
}

export interface Acord25EditPrefill {
  // Insured (stored in the GeneralLiability coverage block, which is always saved)
  insuredName: string;
  insuredAddress: string;
  insuredStreet: string;
  insuredCity: string;
  insuredState: string;
  insuredZip: string;
  // Insurers A–F
  insurerA: string; insurerB: string; insurerC: string;
  insurerD: string; insurerE: string; insurerF: string;
  // GL
  cglInsurerLtr: string;
  cglPolicyNumber: string;
  cglEffDate: Date | null;
  cglExpDate: Date | null;
  cglClaimsMade: boolean;
  cglOccur: boolean;
  cglPolicy: boolean;
  cglProject: boolean;
  cglLoc: boolean;
  cglOther: boolean;
  cglOtherText: string;
  cglBlank1Check: boolean; cglBlank1Text: string;
  cglBlank2Check: boolean; cglBlank2Text: string;
  cglBlankLimitLabel: string; cglBlankLimitAmt: string;
  cglEachOccurrence: string;
  cglDamageRented: string;
  cglMedExp: string;
  cglPersonalAdv: string;
  cglGeneralAgg: string;
  cglProductsAgg: string;
  // Auto
  autoInsurerLtr: string;
  autoPolicyNumber: string;
  autoEffDate: Date | null;
  autoExpDate: Date | null;
  autoAnyAuto: boolean;
  autoAllOwned: boolean;
  autoScheduled: boolean;
  autoHired: boolean;
  autoNonOwned: boolean;
  autoBlank1Check: boolean; autoBlank1Text: string;
  autoBlank2Check: boolean; autoBlank2Text: string;
  autoCombinedSingle: string;
  autoBodilyPerson: string;
  autoBodilyAccident: string;
  autoPropertyDamage: string;
  autoBlankLimitLabel: string; autoBlankLimitAmt: string;
  // Umbrella
  umbrellaInsurerLtr: string;
  umbrellaPolicyNumber: string;
  umbrellaEffDate: Date | null;
  umbrellaExpDate: Date | null;
  umbrellaLiab: boolean;
  excessLiab: boolean;
  umbrellaOccur: boolean;
  umbrellaClaimsMade: boolean;
  umbrellaDed: boolean;
  umbrellaRetentionCheck: boolean;
  umbrellaRetention: string;
  umbrellaEachOccurrence: string;
  umbrellaAggregate: string;
  umbrellaBlankLimitLabel: string; umbrellaBlankLimitAmt: string;
  // WC
  wcInsurerLtr: string;
  wcPolicyNumber: string;
  wcEffDate: Date | null;
  wcExpDate: Date | null;
  wcPerStatute: boolean;
  wcOther: boolean;
  wcOtherText: string;
  wcExcluded: boolean;
  wcEachAccident: string;
  wcDiseaseEmployee: string;
  wcDiseasePolicyLimit: string;
  wcSpecialProvisions: string;
  // Other (slot 1)
  other1InsurerLtr: string;
  other1Type: string;
  other1PolicyNumber: string;
  other1EffDate: Date | null;
  other1ExpDate: Date | null;
  other1LimitLabel1: string;
  other1LimitAmt1: string;
  other1LimitLabel2: string;
  other1LimitAmt2: string;
  other1LimitLabel3: string;
  other1LimitAmt3: string;
  // Carrier databaseIds per insurer slot (for NaicId in payload)
  insurerAId: string | null; insurerBId: string | null; insurerCId: string | null;
  insurerDId: string | null; insurerEId: string | null; insurerFId: string | null;
  // Misc
  certNumber: string;
  revisionNumber: string;
  descriptionOfOps: string;
  certDate: Date | null;
  acord101AdditionalRemarks: string;
  authRepSignature: string;
  // Certificate holder
  holderName: string;
  holderAddress: string;
  holderCity: string;
  holderStreet: string;
  holderState: string;
  holderZip: string;
}

// ── Internal helpers ──────────────────────────────────────────────────────────

function parseDoc(xml: string): Document {
  return new DOMParser().parseFromString(`<root>${xml}</root>`, 'text/xml');
}

function getText(doc: Document, selector: string): string {
  return doc.querySelector(selector)?.textContent?.trim() || '';
}

function yn(v: string): boolean {
  return ['yes', 'true', '1', 'x', 'y'].includes(v.trim().toLowerCase());
}

/** Handles both "MM/DD/YYYY" (from stored XML Date field) and ISO strings. */
function parseStoredDate(v: string | null | undefined): Date | null {
  if (!v) return null;
  const slash = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) return new Date(Number(slash[3]), Number(slash[1]) - 1, Number(slash[2]));
  const iso = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Converts stored master-certificate coverage entries + policy metadata into a
 * flat prefill object that Acord25Form can apply directly to its state.
 */
export function parseAcord25StoredCoverages(
  coverages: StoredCoverage[],
  policies: StoredPolicy[],
): Acord25EditPrefill {
  const result: Acord25EditPrefill = {
    insuredName: '', insuredAddress: '', insuredStreet: '', insuredCity: '', insuredState: '', insuredZip: '',
    insurerA: '', insurerB: '', insurerC: '', insurerD: '', insurerE: '', insurerF: '',
    cglInsurerLtr: '', cglPolicyNumber: '', cglEffDate: null, cglExpDate: null,
    cglClaimsMade: false, cglOccur: false, cglPolicy: false, cglProject: false,
    cglLoc: false, cglOther: false, cglOtherText: '',
    cglBlank1Check: false, cglBlank1Text: '', cglBlank2Check: false, cglBlank2Text: '',
    cglBlankLimitLabel: '', cglBlankLimitAmt: '',
    cglEachOccurrence: '', cglDamageRented: '', cglMedExp: '',
    cglPersonalAdv: '', cglGeneralAgg: '', cglProductsAgg: '',
    autoInsurerLtr: '', autoPolicyNumber: '', autoEffDate: null, autoExpDate: null,
    autoAnyAuto: false, autoAllOwned: false, autoScheduled: false,
    autoHired: false, autoNonOwned: false,
    autoBlank1Check: false, autoBlank1Text: '', autoBlank2Check: false, autoBlank2Text: '',
    autoCombinedSingle: '', autoBodilyPerson: '', autoBodilyAccident: '', autoPropertyDamage: '',
    autoBlankLimitLabel: '', autoBlankLimitAmt: '',
    umbrellaInsurerLtr: '', umbrellaPolicyNumber: '', umbrellaEffDate: null, umbrellaExpDate: null,
    umbrellaLiab: false, excessLiab: false, umbrellaOccur: false, umbrellaClaimsMade: false,
    umbrellaDed: false, umbrellaRetentionCheck: false, umbrellaRetention: '',
    umbrellaEachOccurrence: '', umbrellaAggregate: '',
    umbrellaBlankLimitLabel: '', umbrellaBlankLimitAmt: '',
    wcInsurerLtr: '', wcPolicyNumber: '', wcEffDate: null, wcExpDate: null,
    wcPerStatute: false, wcOther: false, wcOtherText: '', wcExcluded: false,
    wcEachAccident: '', wcDiseaseEmployee: '', wcDiseasePolicyLimit: '', wcSpecialProvisions: '',
    other1InsurerLtr: '', other1Type: '', other1PolicyNumber: '',
    other1EffDate: null, other1ExpDate: null,
    other1LimitLabel1: '', other1LimitAmt1: '', other1LimitLabel2: '',
    other1LimitAmt2: '', other1LimitLabel3: '', other1LimitAmt3: '',
    insurerAId: null, insurerBId: null, insurerCId: null,
    insurerDId: null, insurerEId: null, insurerFId: null,
    certNumber: '', revisionNumber: '', descriptionOfOps: '', certDate: null,
    acord101AdditionalRemarks: '', authRepSignature: '',
    holderName: '', holderAddress: '', holderCity: '', holderStreet: '', holderState: '', holderZip: '',
  };

  /** Store naiCid for the given insurer letter slot (first value wins). */
  const setSlotId = (letter: string, naiCid: string | null) => {
    if (!naiCid) return;
    if (letter === 'A' && !result.insurerAId) result.insurerAId = naiCid;
    else if (letter === 'B' && !result.insurerBId) result.insurerBId = naiCid;
    else if (letter === 'C' && !result.insurerCId) result.insurerCId = naiCid;
    else if (letter === 'D' && !result.insurerDId) result.insurerDId = naiCid;
    else if (letter === 'E' && !result.insurerEId) result.insurerEId = naiCid;
    else if (letter === 'F' && !result.insurerFId) result.insurerFId = naiCid;
  };

  // Build policyNumber → carrierName lookup from policies
  const policyCarrierMap: Record<string, string> = {};
  for (const p of policies) {
    if (p.number && p.carrierName) policyCarrierMap[p.number] = p.carrierName;
  }

  // Assign unique carriers to insurer letter slots A–F in the order they appear.
  // Group by naiCid when present — it's the certificate's actual saved insurer
  // selection — falling back to the linked policy's carrierName only when a
  // coverage entry has no naiCid (legacy certs saved before this field existed).
  // The grouping key may diverge from the display name below; the caller
  // resolves the real name for a naiCid asynchronously and it wins over the
  // carrierName fallback stored here.
  const carrierKeyToLetter: Record<string, string> = {};
  const letters = ['A', 'B', 'C', 'D', 'E', 'F'] as const;
  let nextLetterIdx = 0;
  const insurerFallbackNames: Record<string, string> = {};

  const assignInsurer = (policyNumber: string, naiCid: string | null): string => {
    const fallbackName = policyCarrierMap[policyNumber] || '';
    const key = naiCid || fallbackName;
    if (!key) return '';
    if (carrierKeyToLetter[key]) return carrierKeyToLetter[key];
    if (nextLetterIdx >= 6) return '';
    const letter = letters[nextLetterIdx++];
    carrierKeyToLetter[key] = letter;
    insurerFallbackNames[letter] = fallbackName;
    return letter;
  };

  // Signature: take from first coverage that has one
  for (const cov of coverages) {
    if (cov.signatureId) { result.authRepSignature = cov.signatureId; break; }
  }

  for (const cov of coverages) {
    if (!cov.coveragesAndLimits) continue;

    // ACORD 101 additional remarks
    if (cov.acordFormNumber === 101) {
      const doc = parseDoc(cov.coveragesAndLimits);
      result.acord101AdditionalRemarks = getText(doc, 'Acord101AdditionalRemarks RemarksText');
      continue;
    }

    const doc = parseDoc(cov.coveragesAndLimits);
    const hasCov = (tag: string) => !!doc.querySelector(`Coverages ${tag}`);

    if (hasCov('GeneralLiability')) {
      const letter = assignInsurer(cov.policyNumber, cov.naiCid);
      setSlotId(letter, cov.naiCid);
      result.cglInsurerLtr = letter;
      result.cglPolicyNumber = cov.policyNumber || '';
      result.cglEffDate = parseStoredDate(cov.effectiveDate);
      result.cglExpDate = parseStoredDate(cov.expirationDate);

      result.insuredName    = getText(doc, 'Coverages GeneralLiability Acord25NamedInsuredFullName');
      result.insuredAddress = getText(doc, 'Coverages GeneralLiability Acord25NamedInsuredAddressLineOne');
      result.insuredStreet  = getText(doc, 'Coverages GeneralLiability Acord25NamedInsuredAddressLineTwo');
      result.insuredCity    = getText(doc, 'Coverages GeneralLiability Acord25NamedInsuredCityName');
      result.insuredState   = getText(doc, 'Coverages GeneralLiability Acord25NamedInsuredState');
      result.insuredZip     = getText(doc, 'Coverages GeneralLiability Acord25NamedInsuredPostalCode');

      result.cglClaimsMade = yn(getText(doc, 'Coverages GeneralLiability ClaimsMade'));
      result.cglOccur      = yn(getText(doc, 'Coverages GeneralLiability Occur'));
      result.cglPolicy     = yn(getText(doc, 'Coverages GeneralLiability Policy'));
      result.cglProject    = yn(getText(doc, 'Coverages GeneralLiability Project'));
      result.cglLoc        = yn(getText(doc, 'Coverages GeneralLiability Loc'));
      // <Other> = blank type 1 checkbox; <Other2> = blank type 2; <OtherAggregate> = GEN'L AGG OTHER
      const otherEl = doc.querySelector('Coverages GeneralLiability Other');
      result.cglBlank1Check = yn(otherEl?.textContent || '');
      result.cglBlank1Text  = otherEl?.getAttribute('text') || '';

      const other2El = doc.querySelector('Coverages GeneralLiability Other2');
      result.cglBlank2Check = yn(other2El?.textContent || '');
      result.cglBlank2Text  = other2El?.getAttribute('text') || '';

      const otherAggEl = doc.querySelector('Coverages GeneralLiability OtherAggregate');
      result.cglOther     = yn(otherAggEl?.textContent || '');
      result.cglOtherText = otherAggEl?.getAttribute('text') || '';

      result.cglEachOccurrence = getText(doc, 'Limits GeneralLiability EachOccurrence');
      result.cglDamageRented   = getText(doc, 'Limits GeneralLiability Damage');
      result.cglMedExp         = getText(doc, 'Limits GeneralLiability MedExp');
      result.cglPersonalAdv    = getText(doc, 'Limits GeneralLiability Injury');
      result.cglGeneralAgg     = getText(doc, 'Limits GeneralLiability Aggregate');
      result.cglProductsAgg    = getText(doc, 'Limits GeneralLiability Products');
      const glLimitOtherEl = doc.querySelector('Limits GeneralLiability Other');
      result.cglBlankLimitLabel = glLimitOtherEl?.getAttribute('text') || '';
      result.cglBlankLimitAmt   = glLimitOtherEl?.textContent?.trim() || '';

      // Date from GL section (stored as MM/DD/YYYY)
      const dateStr = getText(doc, 'Coverages GeneralLiability Date');
      if (dateStr) result.certDate = parseStoredDate(dateStr);

      // Certificate / revision number
      result.certNumber     = getText(doc, 'Coverages GeneralLiability CertificateNumber');
      result.revisionNumber = getText(doc, 'Coverages GeneralLiability RevisionNumber');

      // Description of operations (stored in GL block)
      const remarks = getText(doc, 'Coverages GeneralLiability DescriptionOfOperations');
      if (remarks) result.descriptionOfOps = remarks;

    } else if (hasCov('AutomobileLiability')) {
      const letter = assignInsurer(cov.policyNumber, cov.naiCid);
      setSlotId(letter, cov.naiCid);
      result.autoInsurerLtr  = letter;
      result.autoPolicyNumber = cov.policyNumber || '';
      result.autoEffDate      = parseStoredDate(cov.effectiveDate);
      result.autoExpDate      = parseStoredDate(cov.expirationDate);

      result.autoAnyAuto   = yn(getText(doc, 'Coverages AutomobileLiability AnyAuto'));
      result.autoAllOwned  = yn(getText(doc, 'Coverages AutomobileLiability AllOwnedAutos'));
      result.autoScheduled = yn(getText(doc, 'Coverages AutomobileLiability ScheduledAutos'));
      result.autoHired     = yn(getText(doc, 'Coverages AutomobileLiability HiredAutos'));
      result.autoNonOwned  = yn(getText(doc, 'Coverages AutomobileLiability NonOwnedAutos'));

      const blankAuto1El = doc.querySelector('Coverages AutomobileLiability Other');
      result.autoBlank1Check = yn(blankAuto1El?.textContent || '');
      result.autoBlank1Text  = blankAuto1El?.getAttribute('text') || '';
      const blankAuto2El = doc.querySelector('Coverages AutomobileLiability Other2');
      result.autoBlank2Check = yn(blankAuto2El?.textContent || '');
      result.autoBlank2Text  = blankAuto2El?.getAttribute('text') || '';

      // NowCerts stores as CombinedSingleLimit; our builder uses CombinedSingle — handle both
      result.autoCombinedSingle =
        getText(doc, 'Limits AutomobileLiability CombinedSingleLimit') ||
        getText(doc, 'Limits AutomobileLiability CombinedSingle');
      result.autoBodilyPerson   = getText(doc, 'Limits AutomobileLiability BodilyInjuryPerPerson');
      result.autoBodilyAccident = getText(doc, 'Limits AutomobileLiability BodilyInjuryPerAccident');
      result.autoPropertyDamage = getText(doc, 'Limits AutomobileLiability PropertyDamage');
      const autoLimitOtherEl = doc.querySelector('Limits AutomobileLiability Other');
      result.autoBlankLimitLabel = autoLimitOtherEl?.getAttribute('text') || '';
      result.autoBlankLimitAmt   = autoLimitOtherEl?.textContent?.trim() || '';

    } else if (hasCov('UmbrellaLiability')) {
      const letter = assignInsurer(cov.policyNumber, cov.naiCid);
      setSlotId(letter, cov.naiCid);
      result.umbrellaInsurerLtr  = letter;
      result.umbrellaPolicyNumber = cov.policyNumber || '';
      result.umbrellaEffDate      = parseStoredDate(cov.effectiveDate);
      result.umbrellaExpDate      = parseStoredDate(cov.expirationDate);

      result.umbrellaLiab      = yn(getText(doc, 'Coverages UmbrellaLiability Umbrella'));
      result.excessLiab        = yn(getText(doc, 'Coverages UmbrellaLiability Excess'));
      result.umbrellaOccur     = yn(getText(doc, 'Coverages UmbrellaLiability Occur'));
      result.umbrellaClaimsMade = yn(getText(doc, 'Coverages UmbrellaLiability ClaimsMade'));
      result.umbrellaDed       = yn(getText(doc, 'Coverages UmbrellaLiability Ded'));
      const retEl = doc.querySelector('Coverages UmbrellaLiability Retention');
      result.umbrellaRetentionCheck = yn(retEl?.textContent || '');
      result.umbrellaRetention      = retEl?.getAttribute('text') || '';

      result.umbrellaEachOccurrence  = getText(doc, 'Limits UmbrellaLiability EachOccurrence');
      result.umbrellaAggregate       = getText(doc, 'Limits UmbrellaLiability Aggregate');
      const umbLimitOtherEl = doc.querySelector('Limits UmbrellaLiability Other');
      result.umbrellaBlankLimitLabel = umbLimitOtherEl?.getAttribute('text') || '';
      result.umbrellaBlankLimitAmt   = umbLimitOtherEl?.textContent?.trim() || '';

    } else if (hasCov('WorkersCompensation') || hasCov('EmployerLiability')) {
      const letter = assignInsurer(cov.policyNumber, cov.naiCid);
      setSlotId(letter, cov.naiCid);
      result.wcInsurerLtr  = letter;
      result.wcPolicyNumber = cov.policyNumber || '';
      result.wcEffDate      = parseStoredDate(cov.effectiveDate);
      result.wcExpDate      = parseStoredDate(cov.expirationDate);

      if (hasCov('WorkersCompensation')) {
        // NowCerts native stored format
        result.wcPerStatute       = yn(getText(doc, 'Coverages WorkersCompensation PerStatute'));
        result.wcOther            = yn(getText(doc, 'Coverages WorkersCompensation Other'));
        result.wcEachAccident     = getText(doc, 'Limits WorkersCompensation ELEachAccident');
        result.wcDiseaseEmployee  = getText(doc, 'Limits WorkersCompensation ELDiseaseEachEmployee');
        result.wcDiseasePolicyLimit = getText(doc, 'Limits WorkersCompensation ELDiseasePolicyLimit');
      } else {
        // Our sent format (EmployerLiability)
        // <Excluded text="descriptionOfOps">wcSpecialProvisions</Excluded>
        const excludedEl = doc.querySelector('Coverages EmployerLiability Excluded');
        const excludedText = excludedEl?.textContent?.trim() || '';
        result.wcExcluded          = yn(excludedText);
        result.wcSpecialProvisions  = excludedText;
        const excludedAttrText = excludedEl?.getAttribute('text') || '';
        if (excludedAttrText && !result.descriptionOfOps) {
          result.descriptionOfOps = excludedAttrText;
        }
        result.wcPerStatute  = yn(getText(doc, 'Limits EmployerLiability WcStatutory'));
        const otherEl = doc.querySelector('Limits EmployerLiability Other');
        result.wcOther       = yn(otherEl?.getAttribute('text') || '');
        result.wcOtherText   = otherEl?.textContent?.trim() || '';
        result.wcEachAccident       = getText(doc, 'Limits EmployerLiability EachAccident');
        result.wcDiseaseEmployee    = getText(doc, 'Limits EmployerLiability Employee');
        result.wcDiseasePolicyLimit = getText(doc, 'Limits EmployerLiability Policy');
      }

    } else if (hasCov('OtherLiability')) {
      const letter = assignInsurer(cov.policyNumber, cov.naiCid);
      setSlotId(letter, cov.naiCid);
      result.other1InsurerLtr  = letter;
      result.other1PolicyNumber = cov.policyNumber || '';
      result.other1EffDate      = parseStoredDate(cov.effectiveDate);
      result.other1ExpDate      = parseStoredDate(cov.expirationDate);

      // NowCerts stored format uses TypeOfInsurance; our builder uses Text
      result.other1Type =
        getText(doc, 'Coverages OtherLiability TypeOfInsurance') ||
        getText(doc, 'Coverages OtherLiability Text');

      // Limits — NowCerts stores in <Limits> tag; our builder uses CoverageCode/Text
      result.other1LimitLabel1 = getText(doc, 'Limits OtherLiability CoverageCode');
      result.other1LimitAmt1   =
        getText(doc, 'Limits OtherLiability Text') ||
        getText(doc, 'Limits OtherLiability Limits');
      result.other1LimitLabel2 = getText(doc, 'Limits OtherLiability CoverageCode1');
      result.other1LimitAmt2   = getText(doc, 'Limits OtherLiability Text1');
      result.other1LimitLabel3 = getText(doc, 'Limits OtherLiability CoverageCode2');
      result.other1LimitAmt3   = getText(doc, 'Limits OtherLiability Text2');

    } else if (hasCov('Acord101AdditionalRemarks')) {
      result.acord101AdditionalRemarks = getText(doc, 'Acord101AdditionalRemarks RemarksText');
    } else if (hasCov('CertificateHolderInformationCoverage')) {
      result.holderName    = getText(doc, 'CertificateHolderInformationCoverage CertificateHolder Name');
      result.holderAddress = getText(doc, 'CertificateHolderInformationCoverage CertificateHolder Address');
      result.holderCity    = getText(doc, 'CertificateHolderInformationCoverage CertificateHolder City');
      result.holderStreet  = getText(doc, 'CertificateHolderInformationCoverage CertificateHolder Street');
      result.holderState   = getText(doc, 'CertificateHolderInformationCoverage CertificateHolder State');
      result.holderZip     = getText(doc, 'CertificateHolderInformationCoverage CertificateHolder Zip');
    }
  }

  // Populate insurer name slots from the assigned carrier map
  result.insurerA = insurerFallbackNames['A'] || '';
  result.insurerB = insurerFallbackNames['B'] || '';
  result.insurerC = insurerFallbackNames['C'] || '';
  result.insurerD = insurerFallbackNames['D'] || '';
  result.insurerE = insurerFallbackNames['E'] || '';
  result.insurerF = insurerFallbackNames['F'] || '';

  return result;
}
